import {
    XyDataSeries,
    HorizontalLineAnnotation,
    ELabelPlacement,
    EAnnotationLayer,
    VerticalLineAnnotation,
    SciChartSurface,
    ZoomPanModifier,
    MouseWheelZoomModifier,
    ZoomExtentsModifier,
    RolloverModifier,
    RubberBandXyZoomModifier,
    EXyDirection,
    EExecuteOn,
    FastImpulseRenderableSeries,
    LegendModifier,
    EAutoRange,
    NumberRange,
    EAxisAlignment,
    NumericAxis,
    DateTimeNumericAxis,
    CursorModifier,
    FastLineRenderableSeries,
    EllipsePointMarker,
    IDataSeries,
    ISciChartSurfaceBase,
    UpdateSuspender,
    TrianglePointMarker,
    } from "scichart";
  import { IInitResult, TInitFunction } from "scichart-react";
  import { enMessages } from "../../../../../messages/en-US";
  import { ColumnPaletteProvider, ColumnPaletteProviderPvA } from "../PaletteProvider";
  import moment from "moment";
  import { axisLabelDateFormatter } from "../../../../Shared/SciChart/SmartAxis/axisLabelDateFormatter";
  import { GetChartTheme, getTooltipDataTemplate } from "../../../../Shared/SciChart/ChartConfiguration";
import { LocalizationService } from "@progress/kendo-react-intl";
import { DateTickProvider } from "../../../../Shared/SciChart/SmartAxis/DateTickProvider";
import { SmartDateTZLabelProvider } from "../../../../Shared/SciChart/SmartAxis/SmartDateTZLabelProvider";
import { createDateTzConverter } from "../../../../Shared/SciChart/SmartAxis/timezones";
  
  export interface TActivityChartConfigResult<TSurface extends ISciChartSurfaceBase> extends IInitResult<TSurface> {
    setAutoRange: (value: boolean) => void,
    changeChartType: (viewType: string) => void
  }
  
  export type TActivityConfigFunc = TInitFunction<SciChartSurface, TActivityChartConfigResult<SciChartSurface>>;

  export interface Props {
    chartData: any;
  }

  export interface ChartOptions {
    isAutoRange: boolean;
    viewType: string;
    visibleRange: { min: number | undefined; max: number | undefined; };
    setVisibleRange: (range: any) => void;
    setVisibleSeriesActivity: (series: any, isChecked: boolean) => void;
    visibleSeriesActivity: { [key: string]: boolean;}
    aggregator: string;
    isSimpleChart: boolean;
  }
  
  export const getChartAPI = (props:Props, chartOptions:ChartOptions, localizationService: LocalizationService, currentLanguage: { locale: string; localeId: string; }) => {

    const endFilterDateUnix = moment(props.chartData.dateFilter.endDate).unix();
    const startFilterDateUnix = moment(props.chartData.dateFilter.startDate).unix();

    const theme = GetChartTheme();  
    let setAutoRangeCallback: (arg0: boolean) => void;
    let changeChartTypeCallback: (arg0: string) => void;
  
    const configAfterInit = (initResult) => {
        setAutoRangeCallback = initResult.setAutoRange;
        changeChartTypeCallback = initResult.changeChartType;
    } ;
  
    const setAutoRange = (value: boolean) => {
        if (setAutoRangeCallback !== undefined) {
            setAutoRangeCallback(value);
        }
    };

    const changeChartType = (viewType: string) => {
        if (changeChartTypeCallback !== undefined) {
            changeChartTypeCallback(viewType);
        }
    };

    const initChart : TActivityConfigFunc = async (rootElement: string | HTMLDivElement) => {
        const {sciChartSurface, wasmContext} = await SciChartSurface.create(rootElement, {theme: theme});
  
        // #region Setup the XAxis
        const xAxis = new DateTimeNumericAxis(wasmContext, { 
          drawMajorBands: false, 
          drawMinorGridLines: false, 
          drawMajorGridLines: false, 
          visibleRange: new NumberRange(chartOptions.visibleRange?.min, chartOptions.visibleRange?.max),
          visibleRangeLimit: new NumberRange(startFilterDateUnix, endFilterDateUnix),
          growBy: new NumberRange(0.05, 0.05),
          labelStyle: {fontFamily:"Roboto", fontSize:12,}},
          );
  
        // Overrides getDeltaFromRange to get nice major and minor deltas for the ticks
        xAxis.deltaCalculator.getDeltaFromRange = axisLabelDateFormatter.getDeltaFromRange;
  
        const updateSettings = (xAxis: NumericAxis) => {
          const timezone = moment.tz.guess();
          // Calculate ticks which respect the time zone
          xAxis.tickProvider = new DateTickProvider(wasmContext, timezone);
          // Draws nice axis labels timezone and locale specific
          xAxis.labelProvider = new SmartDateTZLabelProvider(timezone,true, currentLanguage?.localeId);
          const dateTzConverter = createDateTzConverter(timezone);
          // This is needed for Cursor and Rollover chart modifiers
          xAxis.labelProvider.formatCursorLabel = timestamp =>
              chartOptions.aggregator === 'none' ? axisLabelDateFormatter.toFullDateTime(timestamp, dateTzConverter, currentLanguage?.localeId) : axisLabelDateFormatter.toFullDateOnly(timestamp, dateTzConverter, currentLanguage?.localeId);
          };
          // Setting timezone to local system
          updateSettings(xAxis);
  
        xAxis.visibleRangeChanged.subscribe((args) => { chartOptions.setVisibleRange(args?.visibleRange); });

        sciChartSurface.xAxes.add(xAxis);
        // #endregion

        // #region Setup the YAxis
        const yAxis = new NumericAxis(wasmContext,{
          axisAlignment: EAxisAlignment.Right, 
          autoRange: EAutoRange.Never,
          drawMajorBands: false,
          drawMinorGridLines: false, 
          drawMajorGridLines: true, 
          labelPostfix: " cc", 
          labelPrecision: 2,
          growBy: new NumberRange(0, 0.2),
          labelStyle: {fontFamily:"Roboto", fontSize:12}});

        sciChartSurface.yAxes.add(yAxis);

        // #endregion

        // #region Setup the Chart Modifers
        const legend = new LegendModifier({
          placementDivId: "legend-activity-flow",
          showCheckboxes: true,
          showSeriesMarkers: true,
          showLegend: !chartOptions.isSimpleChart,
          isCheckedChangedCallback(series, isChecked) {
            chartOptions.setVisibleSeriesActivity(series.id, isChecked);
          },
        });
        sciChartSurface.chartModifiers.add(legend);
 
        sciChartSurface.chartModifiers.add(
          new CursorModifier({ showAxisLabels: false,  id: "ActivityModifierGroup" }),
          new ZoomPanModifier({xyDirection: EXyDirection.XDirection}),
          new MouseWheelZoomModifier({xyDirection:EXyDirection.XDirection}),
          new ZoomExtentsModifier({xyDirection:EXyDirection.XyDirection}),
          new RolloverModifier({tooltipDataTemplate: getTooltipDataTemplate, id: "ActivityRollOver" }),
          new RubberBandXyZoomModifier({ executeOn: EExecuteOn.MouseRightButton }),
        );
        // #endregion

        // #region Create the Data Series 

        const volumeDispensedDataSeries = new XyDataSeries(wasmContext, {
          xValues: props.chartData.timestamps, 
          yValues: props.chartData.volumeDispensedData, 
          dataSeriesName: `${localizationService.toLanguageString('custom.volumeDispensed', enMessages.custom.volumeDispensed)}` 
        });

        const filterPeriodDataSeries = new XyDataSeries(wasmContext, {
          xValues: [startFilterDateUnix, endFilterDateUnix],
          yValues: [0,0], 
          dataSeriesName: "filterPeriodSeries"
        });
        // #endregion

        // #region Setup the RenderableSeries
  
        // Create a series that represents the entire filter period so the chart displays the entire range. 
        const filterPeriodSeries = new FastLineRenderableSeries(wasmContext, {
          id: "FilterPeriodSeriesId", 
          isVisible: false,
          dataSeries: filterPeriodDataSeries
        });  
        sciChartSurface.renderableSeries.add(filterPeriodSeries);
        legend.includeSeries(filterPeriodSeries, false);

        const createVolumeDispensedSeries = (viewType: string, isVisible: boolean, dataSeries: IDataSeries) => {
            let volumeDispensed;
            
            switch(viewType) {
                case 'bar':
                case 'userBar':
                    volumeDispensed = new FastImpulseRenderableSeries(wasmContext, {
                        stroke: "rgba(50, 53, 67,1)", 
                        fill: "rgba(50, 53, 67,1)", 
                        strokeThickness: props.chartData.strokeThickness,
                        paletteProvider: new ColumnPaletteProvider("rgba(50, 53, 67,1)"),
                        pointMarker: new TrianglePointMarker(wasmContext, {width: 12, height: 12, stroke: "rgba(50, 53, 67,1)", fill: "rgba(50, 53, 67,1)"}),
                        id: "VolumeDispensedId",
                        dataSeries: dataSeries,
                        isVisible: isVisible,
                    });
                    break;
                case 'line':
                case 'userLine':
                default:
                    volumeDispensed = new FastLineRenderableSeries(wasmContext, {
                        dataSeries: dataSeries,
                        stroke: "rgba(50, 53, 67,1)", 
                        id: "VolumeDispensedId",
                        isVisible: isVisible,
                        pointMarker: new EllipsePointMarker(wasmContext, {
                            width: 6, 
                            height: 6, 
                            stroke: "rgba(50, 53, 67,1)", 
                            fill: "rgba(50, 53, 67,1)"
                        })
                    });
                    break;
            }

            volumeDispensed.rolloverModifierProps.tooltipTextColor = 'White';
            volumeDispensed.rolloverModifierProps.showRollover = true;
            volumeDispensed.rolloverModifierProps.markerColor = 'rgba(50, 53, 67,1)';
      
            return volumeDispensed;
        }

        const volumeDispensed = createVolumeDispensedSeries(chartOptions.viewType, chartOptions.visibleSeriesActivity.VolumeDispensedId, volumeDispensedDataSeries);
        sciChartSurface.renderableSeries.add(volumeDispensed);

        // #region Last Sync Series
        const lastSyncUtc = props.chartData.lastSyncUtc;
        
        // Only create lastSync series if lastSyncUtc is a valid number
        if (lastSyncUtc !== undefined && lastSyncUtc !== null && !isNaN(lastSyncUtc)) {
          const lastSyncSeries = new FastImpulseRenderableSeries(wasmContext, {
            stroke: "grey", 
            fill: "grey", 
            strokeThickness: props.chartData.strokeThickness, 
            paletteProvider: new ColumnPaletteProviderPvA(), 
            id: "LastSyncId"
          });
          
          lastSyncSeries.dataSeries = new XyDataSeries(wasmContext, {
            xValues: [lastSyncUtc], 
            yValues: [0], 
            dataSeriesName: `${localizationService.toLanguageString('custom.lastSync', enMessages.custom.lastSync)}` 
          });

          lastSyncSeries.rolloverModifierProps.tooltipTextColor = 'White';
          lastSyncSeries.rolloverModifierProps.showRollover = true;
          lastSyncSeries.rolloverModifierProps.markerColor = 'transparent';

          // Add LastSync line annotation
          const lastSyncLine = new VerticalLineAnnotation({
            labelPlacement: ELabelPlacement.TopRight,
            stroke: 'grey',
            strokeThickness: 3,
            strokeDashArray: [12,5],
            x1: lastSyncUtc,
            showLabel: true,
            labelValue: `${localizationService.toLanguageString('custom.lastSync', enMessages.custom.lastSync)}`,
            axisLabelFill: 'grey',
            annotationLayer: EAnnotationLayer.BelowChart
          });

          // Add Last Sync series and annotation if within date range
          if(lastSyncUtc >= startFilterDateUnix && lastSyncUtc <= endFilterDateUnix) {
            sciChartSurface.annotations.add(lastSyncLine)
            sciChartSurface.renderableSeries.add(lastSyncSeries);
            legend.includeSeries(lastSyncSeries, false);
          }
        }
        // #endregion

        // #endregion

        // #region Setup Annotations

        // Add 0 line annotation
        const horizontalLine = new HorizontalLineAnnotation({
          labelPlacement: ELabelPlacement.Axis,
          stroke: 'grey',
          strokeThickness: 3,
          y1: 0,
          annotationLayer: EAnnotationLayer.BelowChart
          });
          sciChartSurface.annotations.add(horizontalLine)
  
          // #endregion

        // #region Setup the API's 
        const setAutoRange = (value: boolean) => {

          const yAxisCurrent = sciChartSurface.yAxes.get(0);
            
          if (yAxisCurrent !== undefined) {
            if (value) {
              yAxisCurrent.autoRange = EAutoRange.Always;
            } else {
              yAxisCurrent.autoRange = EAutoRange.Never;

              const visibleSeries = sciChartSurface.renderableSeries.asArray().filter(x => x.isVisible);

              if (visibleSeries.length === 0) {
                yAxisCurrent.visibleRange = new NumberRange(0, 10 * 1.20);
              } else {

                var minYRange = 0
                var maxYRange = 0;
                
                visibleSeries.forEach((series) => {
                  const dataSeries = series.dataSeries as XyDataSeries;
                  const yRange = dataSeries.getWindowedYRange(dataSeries.xRange, true, false);
                
                  if (yRange !== undefined) {
                    if (yRange.min < minYRange) {
                      minYRange = yRange.min;
                    }

                    if (yRange.max > maxYRange) {
                      maxYRange = yRange.max;
                    }
                  }
                });

                yAxisCurrent.visibleRange = new NumberRange(minYRange, maxYRange * 1.20);
              }
            }
          }
        };
        setAutoRange(chartOptions.isAutoRange);

        const changeChartType = (viewType: string) => {
            UpdateSuspender.using(sciChartSurface, () => {
                const index = sciChartSurface.renderableSeries.asArray().findIndex(x => x.id === "VolumeDispensedId");
                const newVolumeDispensedSeries = createVolumeDispensedSeries(viewType, volumeDispensed.isVisible, volumeDispensed.dataSeries);
                sciChartSurface.renderableSeries.set(index, newVolumeDispensedSeries);
            });
        }
        // #endregion
  
      return { sciChartSurface: sciChartSurface, setAutoRange, changeChartType};
    }

    return {initChart, configAfterInit, setAutoRange, changeChartType};
}